import type { AccountStatus } from '@/account/AccountService';
import type { TeaserMode } from '@/config/entitlement';

/**
 * entitlement.ts — the PAID-GATE rule, PURE + unit-tested (the M111
 * `shouldFrontAccountGate` shape: a tiny predicate over primitives, so every
 * call site can be a one-liner and every rule has a test instead of a comment).
 *
 * The gate is a PAYWALL, NOT DRM. A web bundle is downloadable and this rule
 * runs on the client; online multiplayer is the only server-enforceable
 * surface, which is also the only surface that costs money to run. The job here
 * is to ask an honest player to buy, and — far more important — to NEVER ask a
 * player who has already paid.
 */

/** Everything the paid gate is allowed to look at. */
export interface PurchaseGateInput {
  /** `ENTITLEMENT_ENABLED` — the paywall kill switch (config/entitlement.ts). */
  entitlementEnabled: boolean;
  /**
   * M124 — `isNativeApp()`: running inside the iOS / Android app shell.
   * **The APPS ARE PAID UP FRONT on their store** (the $9.99 is the App Store /
   * Play listing price), so a player inside the app has ALREADY BOUGHT the
   * game — there is nothing left to sell them, and Apple guideline 3.1.1
   * forbids pointing them at an outside storefront anyway. `true` = never
   * gate. Optional (defaults false) so every existing web-side caller and
   * test reads unchanged.
   */
  native?: boolean;
  /** `TEASER_MODE` — the build variant. */
  teaser: TeaserMode;
  /** `SUPABASE_ENABLED` — the account layer's kill switch (config/supabase.ts). */
  accountEnabled: boolean;
  /** The LIVE account status (AccountState.status). */
  status: AccountStatus;
  /**
   * Server-resolved ownership for the signed-in account: the wallet's
   * `owns_game`. **`null` = the wallet has not answered yet** — it is NOT
   * "does not own".
   */
  owns: boolean | null;
  /**
   * OFFLINE GRACE — ownership as last mirrored into the local save for THIS
   * device's account. Used only while the server is silent (see below).
   */
  cachedOwns?: boolean;
}

/**
 * Should the PURCHASE screen be fronted right now?
 *
 * True only when ALL of these hold — each one is a separate way to accidentally
 * paywall a paying customer, which is the only truly expensive failure here:
 *
 * 1. `entitlementEnabled` — off by default; one line turns the whole feature off.
 *
 * 1b. NOT the native app (M124). The iOS / Android apps are PAID LISTINGS —
 *    the store already collected the price before the app could launch, so
 *    inside the shell every player is an owner by construction, whatever
 *    their Racer ID's web entitlement says (an app buyer may well have NO
 *    Stripe row). The apps still REQUIRE a Racer ID (the M111 boot gate,
 *    unchanged) — that is an account gate, not a paywall. Cross-store
 *    unification (an App Store receipt writing an `entitlements` row so the
 *    same Racer ID also owns the WEB game) is the recorded fast-follow; until
 *    it lands, "native" simply means "bought".
 *
 * 2. NOT a teaser build — the teaser's whole job is to be played for free. (Its
 *    own soft replay cap is `shouldFrontTeaserPurchase` below, not this gate.)
 *
 * 3. `accountEnabled` — **critical, and the least obvious rule here.**
 *    Entitlement lives on the Racer ID; if the account layer is killed the game
 *    literally CANNOT KNOW who owns it. That kill switch must therefore restore
 *    FULL ACCESS, not lock every buyer out of the game they paid for. A rollback
 *    lever that bricks the product is not a rollback lever.
 *
 * 4. The account status is RESOLVED. `'loading'` NEVER gates — the M111 lesson,
 *    verbatim: the supabase chunk lands ~400 ms after boot, and gating in that
 *    window flashes a paywall at a paying customer every single launch.
 *    `'disabled'` never gates either (it is case 3 arriving as a status).
 *
 * 5. Ownership is genuinely absent:
 *    - signed IN: gate only on a definitive server `owns === false`. `null`
 *      never gates, for exactly the reason `'loading'` never gates — an
 *      unanswered wallet is not a negative answer.
 *    - signed OUT: gate, UNLESS the offline-grace cache says this device last
 *      saw an owner (a paying customer on a plane whose session refresh needs a
 *      network it does not have must still be able to play).
 *
 * NOTE the asymmetry in rule 5: a live server `false` BEATS the cached value
 * (that is how a refund or a revocation takes effect), while the cached value
 * only ever fills SILENCE. Same doctrine as everywhere else in this codebase —
 * the server is the authority whenever it has spoken.
 *
 * THE CACHED VALUE IS LOCALLY FORGEABLE. Anyone can edit their own save and
 * claim ownership. Accepted, deliberately: it is the same class as the existing
 * M100 client-assertion gaps (`?veh=`, client-asserted names), the alternative
 * is paywalling honest offline buyers, and ONLINE PLAY STAYS SERVER-GATED —
 * which is where the real cost lives. CALLER CONTRACT: clear the cached value
 * on an explicit sign-out and on a user switch (the M103 never-leak rule), so
 * the grace only ever covers "the session could not refresh", never "somebody
 * else's device remembers an owner".
 */
export function shouldFrontPurchaseGate(input: PurchaseGateInput): boolean {
  const { entitlementEnabled, native, teaser, accountEnabled, status, owns, cachedOwns } = input;

  if (!entitlementEnabled) return false;
  if (native === true) return false;
  if (teaser !== 'off') return false;
  if (!accountEnabled) return false;

  switch (status) {
    case 'signedIn':
      // null (wallet silent) and true both mean "do not gate".
      return owns === false;
    case 'signedOut':
      return cachedOwns !== true;
    default:
      // 'loading' + 'disabled' — unresolved. Never gate.
      return false;
  }
}

/**
 * The TEASER's soft replay cap: after `cap` finished races, front the purchase
 * screen (with a keep-playing escape — this is a nudge, not a wall).
 *
 * Only the `'web'` teaser ever fronts it: `'portal'` builds must show no
 * purchase UI at all, and `'off'` is the full game, which has no cap. A cap of
 * 0 (or less) disables the nudge entirely.
 *
 * The caller increments the counter in the OFFLINE branch of `onRaceFinished`,
 * which means the How-To-Play tutorial demo (which returns earlier) is uncapped
 * BY CONSTRUCTION — exactly right: the playable tutorial is the single best
 * conversion asset in the game.
 */
export function shouldFrontTeaserPurchase(
  teaser: TeaserMode,
  racesPlayed: number,
  cap: number,
): boolean {
  if (teaser !== 'web') return false;
  if (cap <= 0) return false;
  return racesPlayed >= cap;
}
